import { Injectable, inject } from '@angular/core';
import {
  CoreActionLogout,
  CoreActions,
  CoreActionSetBiometricPassword,
  CoreActionUpdateLogin,
  CoreActionUpdateUserLanguage,
  CorePopupService,
  CoreRouterActionGoBack,
  CoreRouterActionNavigateForward,
  CoreRouterActions,
  CoreUtilsService,
  selectorCoreAppVersion,
  selectorCoreInstanceInfo
} from '@bepatient/core';
import {
  CoreMobiResourceType,
  CoreActionNavigateToResource
} from '@bepatient/core';
import { Device } from '@capacitor/device';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, from, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import { selectorMyAccountUserLanguageChanged } from './my-account.reducer';
import { ChangeEmailPopupComponent } from './components/change-email-popup/change-email-popup.component';
import { ChangePasswordSuccessPopupComponent } from './components/change-password-success-popup/change-password-success-popup.component';
import { MyAccountApiService } from './my-account-api.service';
import {
  MyAccountActionLoadFormsFailed,
  MyAccountActionLoadFormsSuccess,
  MyAccountActionLoadTosItemsFailed,
  MyAccountActionLoadTosItemsSuccess,
  MyAccountActions,
  MyAccountActionSetNewPassword,
  MyAccountActionSetNewPasswordFailed,
  MyAccountActionSetNewPasswordSuccess,
  MyAccountActionsTypes,
  MyAccountActionLoadFormHrefFailed,
  MyAccountActionLoadFormHref,
  MyAccountActionSetNewEmail,
  MyAccountActionSetNewEmailSuccess,
  MyAccountActionSetNewEmailFailed,
  MyAccountActionLoadTosItemDetails,
  MyAccountActionLoadTosItemDetailsSuccess,
  MyAccountActionLoadTosItemDetailsFailed,
  MyAccountActionLoadTosItemsV2,
  MyAccountActionLoadTosItemsV2Success,
  MyAccountActionLoadTosItemsV2Failed,
  MyAccountActionLoadDocument,
  MyAccountActionLoadDocumentSuccess,
  MyAccountActionLoadDocumentFailed,
  MyAccountActionSetUserPreference,
  MyAccountActionSetUserPreferenceFailed,
  MyAccountActionSetUserPreferenceSuccess,
  MyAccountActionLoadForms,
  MyAccountActionLoadHelpSuccess,
  MyAccountActionLoadHelpFailed
} from './my-account.actions';

@Injectable()
export class MyAccountEffects {
  private action$ = inject(Actions);
  private store$ = inject<Store<any>>(Store);
  private myAccountApiService = inject(MyAccountApiService);
  private popup = inject(CorePopupService);
  private translate = inject(TranslateService);
  private coreUtils = inject(CoreUtilsService);

  loadForms$: Observable<MyAccountActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_FORMS),
      switchMap(() =>
        this.myAccountApiService.loadForms().pipe(
          map(res => new MyAccountActionLoadFormsSuccess(res)),
          catchError(() => of(new MyAccountActionLoadFormsFailed()))
        )
      )
    )
  );

  loadMyAccountTosItems$: Observable<MyAccountActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_TOS_ITEMS),
      switchMap(() =>
        this.myAccountApiService.loadTosItems().pipe(
          map(tosItems => new MyAccountActionLoadTosItemsSuccess(tosItems)),
          catchError(() => of(new MyAccountActionLoadTosItemsFailed()))
        )
      )
    )
  );

  loadMyAccountTosItemsV2$: Observable<MyAccountActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_TOS_ITEMS_V2),
      map((action: MyAccountActionLoadTosItemsV2) => action.payload),
      switchMap(url =>
        this.myAccountApiService.loadTosItems(url).pipe(
          map(tosItems => new MyAccountActionLoadTosItemsV2Success(tosItems)),
          catchError(() => of(new MyAccountActionLoadTosItemsV2Failed()))
        )
      )
    )
  );

  setSelectedTosIndex$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.SET_SELECTED_TOS_INDEX),
      map(
        () =>
          new CoreRouterActionNavigateForward({
            url: 'my-account-tos-details'
          })
      )
    )
  );

  loadMyAccountTosDetails$: Observable<any> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_TOS_ITEM_DETAILS),
      map((action: MyAccountActionLoadTosItemDetails) => action.payload),
      switchMap(url =>
        this.myAccountApiService.loadTosDetails(url).pipe(
          map(data => {
            return new MyAccountActionLoadTosItemDetailsSuccess(data);
          }),
          catchError(() => of(new MyAccountActionLoadTosItemDetailsFailed()))
        )
      )
    )
  );

  setNewPassword$: Observable<MyAccountActions | CoreActions> = createEffect(
    () =>
      this.action$.pipe(
        ofType(MyAccountActionsTypes.SET_NEW_PASSWORD),
        map((action: MyAccountActionSetNewPassword) => action.payload),
        // Set the current viewContainerRef for the instance of the popup service to create the popup.
        tap(payload => {
          this.popup.popupServiceRef = payload.viewContainerRef;
        }),
        withLatestFrom(
          this.store$.pipe(select(selectorCoreAppVersion)),
          from(Device.getInfo()),
          (payload, appVersion, device: any) => [payload, appVersion, device]
        ),
        switchMap(([payload, appVersion, device]) => {
          const deviceType = device.platform || void 0;
          return this.myAccountApiService
            .setNewPassword(payload.request, deviceType, appVersion)
            .pipe(
              switchMap(res => [
                new CoreActionUpdateLogin({
                  ...res,
                  userChangedPassword: true
                }),
                new MyAccountActionSetNewPasswordSuccess(),
                new CoreActionSetBiometricPassword({
                  password: payload?.request?.newPassword
                })
              ]),
              catchError(() => of(new MyAccountActionSetNewPasswordFailed()))
            );
        })
      )
  );

  setNewPasswordSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.SET_NEW_PASSWORD_SUCCESS),
      withLatestFrom(this.store$.pipe(select(selectorCoreInstanceInfo))),
      switchMap(
        ([, { appColor: color }]) =>
          new Observable<CoreRouterActions>(subscriber => {
            this.popup
              .create(ChangePasswordSuccessPopupComponent, {
                inputs: {
                  color,
                  message: this.translate.instant(
                    'MY_ACCOUNT.PASSWORD.CHANGED_SUCCESS'
                  ),
                  buttonLabel: this.translate.instant('OK')
                }
              })
              .then(component => {
                component.validated.pipe(take(1)).subscribe(() => {
                  subscriber.next(new CoreRouterActionGoBack());
                  subscriber.complete();
                  this.popup.dismiss();
                });
              });
          })
      )
    )
  );

  loadForm$: Observable<CoreActions | MyAccountActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_FORM_HREF),
      map((action: MyAccountActionLoadFormHref) => action.payload),
      switchMap(url =>
        this.myAccountApiService.loadFormHref(url).pipe(
          map(
            href =>
              new CoreActionNavigateToResource({
                resource: { type: CoreMobiResourceType.FORM, href: href }
              })
          ),
          catchError(() => of(new MyAccountActionLoadFormHrefFailed()))
        )
      )
    )
  );

  loadDocumentDetails$: Observable<MyAccountActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_DOCUMENT),
      map((action: MyAccountActionLoadDocument) => action.payload),
      switchMap((url: string) =>
        this.myAccountApiService.loadDocumentHref(url).pipe(
          switchMap(res => of(new MyAccountActionLoadDocumentSuccess(res))),
          catchError(() => of(new MyAccountActionLoadDocumentFailed()))
        )
      )
    )
  );

  setNewEmail$: Observable<MyAccountActions | CoreActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.SET_NEW_EMAIL),
      map((action: MyAccountActionSetNewEmail) => action.payload),
      switchMap(data =>
        this.myAccountApiService
          .setNewEmail(data.oldEmail, data.newEmail, data.newEmailConfirmation)
          .pipe(
            switchMap(res =>
              of(new MyAccountActionSetNewEmailSuccess(res.success.msg))
            ),
            catchError(res =>
              of(new MyAccountActionSetNewEmailFailed(res.error.msg))
            )
          )
      )
    )
  );

  setNewEmailFailed$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.SET_NEW_EMAIL_FAILED),
      map((action: MyAccountActionSetNewEmailFailed) => action.payload),
      withLatestFrom(this.store$.pipe(select(selectorCoreInstanceInfo))),
      switchMap(
        ([payload, { appColor: color }]) =>
          new Observable<CoreRouterActions>(subscriber => {
            this.popup
              .create(ChangeEmailPopupComponent, {
                inputs: {
                  color,
                  isSuccess: false,
                  message: payload,
                  buttonLabel: this.translate.instant('OK')
                }
              })
              .then(component => {
                component.validated.pipe(take(1)).subscribe(() => {
                  subscriber.complete();
                  this.popup.dismiss();
                });
              });
          })
      )
    )
  );

  setNewEmailSuccess$: Observable<CoreRouterActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.SET_NEW_EMAIL_SUCCESS),
      map((action: MyAccountActionSetNewEmailSuccess) => action.payload),
      withLatestFrom(this.store$.pipe(select(selectorCoreInstanceInfo))),
      switchMap(
        ([payload, { appColor: color }]) =>
          new Observable<CoreRouterActions>(subscriber => {
            this.popup
              .create(ChangeEmailPopupComponent, {
                inputs: {
                  color,
                  isSuccess: true,
                  message: payload,
                  buttonLabel: this.translate.instant('OK')
                }
              })
              .then(component => {
                component.validated.pipe(take(1)).subscribe(() => {
                  this.store$.dispatch(new CoreActionLogout());
                  subscriber.complete();
                  this.popup.dismiss();
                });
              });
          })
      )
    )
  );

  setUserPreference$: Observable<
    MyAccountActions | CoreActions | CoreRouterActions
  > = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.SET_USER_PREFERENCE),
      map((action: MyAccountActionSetUserPreference) => action.payload),
      switchMap(preferenceData =>
        this.myAccountApiService.setUserPreference(preferenceData).pipe(
          withLatestFrom(
            this.store$.pipe(select(selectorMyAccountUserLanguageChanged))
          ),
          switchMap(([res, language]) =>
            language
              ? [
                  // Display success Toast
                  new MyAccountActionSetUserPreferenceSuccess(res),
                  // Update core login user language
                  new CoreActionUpdateLogin({ language }),
                  // Set user selected language in App
                  new CoreActionUpdateUserLanguage({
                    language: language,
                    rightToLeft: language ? language === 'ar' : false
                  }),
                  // Reload my account list
                  new MyAccountActionLoadForms(),
                  // Send user back to my account page
                  new CoreRouterActionGoBack()
                ]
              : [
                  // Reload my account list
                  new MyAccountActionLoadForms(),
                  new CoreRouterActionGoBack(),
                  new MyAccountActionSetUserPreferenceSuccess(res)
                ]
          ),
          catchError(() => of(new MyAccountActionSetUserPreferenceFailed()))
        )
      )
    )
  );

  setUserPreferenceSuccess$: Observable<MyAccountActions> = createEffect(
    () =>
      this.action$.pipe(
        ofType(MyAccountActionsTypes.SET_USER_PREFERENCE_SUCCESS),
        map(
          (action: MyAccountActionSetUserPreferenceSuccess) => action.payload
        ),
        tap(payload => {
          if (payload) {
            this.coreUtils.presentToaster(
              payload.message ||
                this.translate.instant(
                  'MY_ACCOUNT.USER_PREFERENCE.SAVE_PREFERENCE_SUCCESS'
                )
            );
          }
        })
      ),
    { dispatch: false }
  );

  loadHelpContent$: Observable<MyAccountActions> = createEffect(() =>
    this.action$.pipe(
      ofType(MyAccountActionsTypes.LOAD_HELP),
      switchMap(() =>
        this.myAccountApiService.loadHelpContent().pipe(
          map(res => new MyAccountActionLoadHelpSuccess(res)),
          catchError(() => of(new MyAccountActionLoadHelpFailed()))
        )
      )
    )
  );
}
